"use client";

import { useState, type FormEvent } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AnimatedButton } from "@/components/marketing/AnimatedButton";
import { PhosphorIcon } from "@/components/marketing/PhosphorIcon";
import { trackAnalyticsEvent } from "@/lib/analytics/track";

const API = "/api/waitlist/mobile-app";

export function MobileAppWaitlistForm(): React.ReactElement {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<
    "idle" | "submitting" | "success" | "error"
  >("idle");
  const [errorMessage, setErrorMessage] = useState("");

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setStatus("submitting");
    setErrorMessage("");

    try {
      const res = await fetch(API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: name.trim(), email: email.trim() }),
      });

      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as {
          error?: string;
        };
        const msg = body.error ?? "Something went wrong. Please try again.";
        throw new Error(msg);
      }

      setStatus("success");
      trackAnalyticsEvent("submit_mobile_app_waitlist", {});
      setName("");
      setEmail("");
    } catch (err) {
      setStatus("error");
      setErrorMessage(
        err instanceof Error ? err.message : "Something went wrong."
      );
      trackAnalyticsEvent("mobile_app_waitlist_error", {
        error_code: err instanceof Error ? err.message : "unknown",
      });
    }
  }

  if (status === "success") {
    return (
      <motion.div
        className="rounded-lg border border-primary/20 bg-primary/5 p-6 text-center"
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.25 }}
      >
        <div className="mx-auto mb-2 flex justify-center">
          <PhosphorIcon
            name="check-circle"
            className="h-10 w-10 text-primary"
            aria-hidden="true"
          />
        </div>
        <p className="text-lg font-semibold text-foreground">
          You&apos;re on the mobile list!
        </p>
        <p className="mt-1 text-muted-foreground">
          We&apos;ll email you when native app are ready.
        </p>
      </motion.div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="mx-auto flex max-w-md flex-col gap-4"
    >
      <div className="grid gap-2">
        <Label htmlFor="mobile-waitlist-name">Name</Label>
        <Input
          id="mobile-waitlist-name"
          type="text"
          placeholder="Your name"
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={status === "submitting"}
        />
      </div>
      <div className="grid gap-2">
        <Label htmlFor="mobile-waitlist-email">Email</Label>
        <Input
          id="mobile-waitlist-email"
          type="email"
          placeholder="you@restaurant.com"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={status === "submitting"}
        />
      </div>
      <AnimatePresence>
        {status === "error" && (
          <motion.p
            className="text-sm text-destructive"
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
          >
            {errorMessage}
          </motion.p>
        )}
      </AnimatePresence>
      <AnimatedButton>
        <Button type="submit" size="lg" disabled={status === "submitting"}>
          {status === "submitting" ? "Joining..." : "Join the mobile waitlist"}
        </Button>
      </AnimatedButton>
    </form>
  );
}
